// CRAM Task 6 — preview-before-save modal.
//
// Opened by CramListEditor after it calls `cramPreviewRecalc` (which created a
// cram_recalc_jobs/{jobId} doc). We subscribe to that job doc and walk the
// status machine:
//   previewing → spinner while the worker recomputes every CRAM customer.
//   preview    → show the affected-customer movements + a capped sample, with
//                Confirm (→ cramApplyRecalc) and Discard buttons.
//   applying   → spinner with live processed/changed counts.
//   applied    → success; we clear the editor's local buffer (onApplied).
//   failed     → error alert.
// Discard writes status="discarded" straight to the job doc (admin/CRAM rule)
// and leaves Firestore lists untouched.
import React, { useEffect, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Group,
    Loader,
    Modal,
    ScrollArea,
    Stack,
    Table,
    Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
    IconAlertCircle,
    IconArrowRight,
    IconCheck,
} from "@tabler/icons-react";
import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useFirestore, useFunctions } from "../../../helpers/firebaseContext";
import { CramRecalcJob } from "../../../types";
import { ratingBadgeColor } from "../cramListsRegistry";

interface Props {
    jobId: string | null;
    listLabel: string;
    onClose: () => void;
    // Called once when the job reaches "applied" so the editor can drop its
    // pending-change buffer (the list is now the new source of truth).
    onApplied: () => void;
}

const CramRecalcPreviewModal: React.FC<Props> = ({
    jobId,
    listLabel,
    onClose,
    onApplied,
}) => {
    const db = useFirestore();
    const functions = useFunctions();
    const [job, setJob] = useState<CramRecalcJob | null>(null);
    const [jobMissing, setJobMissing] = useState(false);
    const [applying, setApplying] = useState(false);
    const [appliedHandled, setAppliedHandled] = useState(false);

    useEffect(() => {
        if (!jobId) {
            setJob(null);
            setJobMissing(false);
            setApplying(false);
            setAppliedHandled(false);
            return;
        }
        const unsub = onSnapshot(
            doc(db, "cram_recalc_jobs", jobId),
            (snap) => {
                // The job doc is created before this modal opens, so a missing
                // doc means it was deleted/never persisted — surface an error
                // instead of spinning forever.
                if (!snap.exists()) {
                    setJob(null);
                    setJobMissing(true);
                    return;
                }
                setJobMissing(false);
                setJob(snap.data() as CramRecalcJob);
            },
            (err) => {
                console.error("[CRAM] recalc job snapshot error:", err);
                notifications.show({
                    title: "Failed to load preview",
                    message: err.message,
                    color: "red",
                    icon: <IconAlertCircle size={16} />,
                });
            }
        );
        return () => unsub();
    }, [db, jobId]);

    // Clear the editor buffer once when the job lands on "applied".
    useEffect(() => {
        if (job?.status === "applied" && !appliedHandled) {
            setAppliedHandled(true);
            onApplied();
        }
    }, [job?.status, appliedHandled, onApplied]);

    // Reset the local applying flag once the job reaches a terminal state, so a
    // failed/applied job doesn't leave the Confirm button stuck in a loading
    // state if the modal stays open.
    useEffect(() => {
        if (
            job?.status === "applied" ||
            job?.status === "failed" ||
            job?.status === "discarded"
        ) {
            setApplying(false);
        }
    }, [job?.status]);

    const handleConfirm = async () => {
        if (!jobId) return;
        setApplying(true);
        try {
            const fn = httpsCallable(functions, "cramApplyRecalc");
            await fn({ jobId });
        } catch (err: any) {
            console.error("[CRAM] apply error:", err);
            notifications.show({
                title: "Apply failed",
                message: err?.message ?? "Unknown error",
                color: "red",
                icon: <IconAlertCircle size={16} />,
            });
            setApplying(false);
        }
    };

    const handleDiscard = async () => {
        if (jobId && job && job.status === "preview") {
            try {
                await setDoc(
                    doc(db, "cram_recalc_jobs", jobId),
                    { status: "discarded", updatedAt: serverTimestamp() },
                    { merge: true }
                );
            } catch (err) {
                console.warn("[CRAM] discard failed:", err);
            }
        }
        onClose();
    };

    const status = job?.status;
    const preview = job?.preview;

    // Closing is blocked while a recompute/apply is in flight (spinner states)
    // so the user doesn't abandon the job and lose the results. In "preview" we
    // discard the pending job; otherwise (terminal or missing) we just close.
    const handleModalClose = () => {
        if (status === "previewing" || status === "applying") return;
        if (status === "preview") {
            void handleDiscard();
            return;
        }
        onClose();
    };

    return (
        <Modal
            opened={!!jobId}
            onClose={handleModalClose}
            title={`Impact preview — ${listLabel}`}
            size="lg"
            closeOnClickOutside={false}
        >
            <Stack gap="md">
                {jobMissing && (
                    <Alert
                        icon={<IconAlertCircle size={16} />}
                        color="red"
                        variant="light"
                    >
                        Preview job not found. It may have been removed. No
                        changes were applied.
                    </Alert>
                )}

                {!jobMissing && (!job || status === "previewing") && (
                    <Group justify="center" py="xl" gap="sm">
                        <Loader size="sm" />
                        <Text c="dimmed">
                            Recalculating every CRAM customer against the
                            proposed list&hellip;
                        </Text>
                    </Group>
                )}

                {status === "applying" && (
                    <Group justify="center" py="xl" gap="sm">
                        <Loader size="sm" />
                        <Text c="dimmed">
                            Applying&hellip;{" "}
                            {job?.apply?.changedCount ?? 0} updated of{" "}
                            {job?.apply?.processedCount ?? 0} processed.
                        </Text>
                    </Group>
                )}

                {status === "applied" && (
                    <Alert
                        icon={<IconCheck size={16} />}
                        color="green"
                        variant="light"
                    >
                        Applied. {job?.apply?.changedCount ?? 0} customer
                        {(job?.apply?.changedCount ?? 0) === 1 ? "" : "s"} had
                        their CRAM rating updated.
                    </Alert>
                )}

                {status === "failed" && (
                    <Alert
                        icon={<IconAlertCircle size={16} />}
                        color="red"
                        variant="light"
                    >
                        Recalculation failed: {job?.error || "unknown error"}. If
                        it failed while applying, some list changes may already
                        be saved — re-open the list to verify before retrying.
                    </Alert>
                )}

                {status === "discarded" && (
                    <Alert color="gray" variant="light">
                        Discarded — no changes were applied.
                    </Alert>
                )}

                {status === "preview" && preview && (
                    <>
                        <Text size="sm">
                            <Text span fw={600}>
                                {preview.totalAffected}
                            </Text>{" "}
                            of {preview.totalCandidates} CRAM customer
                            {preview.totalCandidates === 1 ? "" : "s"} would
                            change rating if you save this list.
                        </Text>

                        {preview.movements.length > 0 ? (
                            <Group gap="xs">
                                {preview.movements.map((m, i) => (
                                    <Badge
                                        key={i}
                                        variant="light"
                                        color={ratingBadgeColor(m.to)}
                                        leftSection={
                                            <Group gap={2} wrap="nowrap">
                                                <Text span size="xs">
                                                    {m.from}
                                                </Text>
                                                <IconArrowRight size={10} />
                                                <Text span size="xs">
                                                    {m.to}
                                                </Text>
                                            </Group>
                                        }
                                    >
                                        {m.count}
                                    </Badge>
                                ))}
                            </Group>
                        ) : (
                            <Text size="sm" c="dimmed">
                                No customer ratings change with this edit.
                            </Text>
                        )}

                        {!!preview.scoreOnlyAffected && preview.scoreOnlyAffected > 0 && (
                            <Text size="sm" c="dimmed">
                                {preview.scoreOnlyAffected} more customer
                                {preview.scoreOnlyAffected === 1 ? "" : "s"} keep the
                                same rating but their score and breakdown will be
                                refreshed.
                            </Text>
                        )}

                        {preview.sample.length > 0 && (
                            <ScrollArea.Autosize mah={280}>
                                <Table striped withTableBorder>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Customer</Table.Th>
                                            <Table.Th>From</Table.Th>
                                            <Table.Th>To</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {preview.sample.map((s) => (
                                            <Table.Tr key={s.cid}>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {s.name || s.cid}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Badge
                                                        size="sm"
                                                        variant="light"
                                                        color={ratingBadgeColor(
                                                            s.oldRating
                                                        )}
                                                    >
                                                        {s.oldRating}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Badge
                                                        size="sm"
                                                        variant="light"
                                                        color={ratingBadgeColor(
                                                            s.newRating
                                                        )}
                                                    >
                                                        {s.newRating}
                                                    </Badge>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea.Autosize>
                        )}

                        {preview.totalAffected > preview.sample.length && (
                            <Text size="xs" c="dimmed">
                                Showing the first {preview.sample.length} of{" "}
                                {preview.totalAffected} affected customers.
                            </Text>
                        )}
                    </>
                )}

                <Group justify="flex-end">
                    {status === "preview" ? (
                        <>
                            <Button
                                variant="default"
                                onClick={handleDiscard}
                                disabled={applying}
                            >
                                Discard
                            </Button>
                            <Button
                                onClick={handleConfirm}
                                loading={applying}
                                leftSection={<IconCheck size={16} />}
                            >
                                Confirm &amp; apply
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="default"
                            onClick={handleModalClose}
                            disabled={
                                status === "previewing" || status === "applying"
                            }
                        >
                            Close
                        </Button>
                    )}
                </Group>
            </Stack>
        </Modal>
    );
};

export default CramRecalcPreviewModal;
